import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface SystemStatus {
  api: 'online' | 'offline' | 'loading';
  database: 'connected' | 'disconnected' | 'loading';
  functions: 'deployed' | 'error' | 'loading';
  lastCheck: string;
}

interface DetailedHealthInfo {
  version: string;
  uptime: number;
  region: string;
  nodeVersion: string;
  databaseId: string;
  timestamp: string;
  projectId?: string;
  environment?: string;
  endpoints?: Array<{
    method: string;
    path: string;
    description: string;
  }>;
  metrics?: {
    activeConnections: number;
  };
}

interface HealthMetrics {
  responseTime: number;
  activeConnections: number;
}

export function Health() {
  const { currentUser } = useAuth();
  const [systemStatus, setSystemStatus] = useState<SystemStatus>({
    api: 'loading',
    database: 'loading',
    functions: 'loading',
    lastCheck: new Date().toLocaleString('nl-NL')
  });
  const [healthInfo, setHealthInfo] = useState<DetailedHealthInfo | null>(null);
  const [metrics, setMetrics] = useState<HealthMetrics>({
    responseTime: 0,
    activeConnections: 0
  });
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const getAuthHeaders = async () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get ID token:', error);
      }
    }
    
    return headers;
  };

  const checkSystemHealth = async () => {
    setLoading(true);
    const startTime = Date.now();
    
    try {
      const response = await fetch('https://license.s3e.nl/health');
      const responseTime = Date.now() - startTime;
      
      if (response.ok) {
        const data = await response.json();
        setSystemStatus(prev => ({
          ...prev,
          api: 'online',
          database: data.database === 'connected' ? 'connected' : 'disconnected',
          functions: 'deployed',
          lastCheck: new Date().toLocaleString('nl-NL')
        }));
        
        setMetrics(prev => ({
          ...prev,
          responseTime,
          activeConnections: 0 // Will be updated from detailed health info
        }));
        
        try {
          const detailedResponse = await fetch('https://license.s3e.nl/admin/health', {
            headers: await getAuthHeaders()
          });
          if (detailedResponse.ok) {
            const detailedData = await detailedResponse.json();
            setHealthInfo({
              version: detailedData.version || '1.0.0',
              uptime: detailedData.uptime || 0,
              region: detailedData.region || 'europe-west1',
              nodeVersion: detailedData.nodeVersion || 'Unknown',
              databaseId: detailedData.databaseId || 'default',
              timestamp: detailedData.timestamp || new Date().toISOString(),
              projectId: detailedData.projectId,
              environment: detailedData.environment,
              endpoints: detailedData.endpoints
            });
            
            // Update metrics with real data from backend
            if (detailedData.metrics) {
              setMetrics(prev => ({
                ...prev,
                activeConnections: detailedData.metrics.activeConnections || 0
              }));
            }
          }
        } catch (error) {
          setHealthInfo({
            version: '1.0.0',
            uptime: 0,
            region: 'europe-west1',
            nodeVersion: 'Node.js',
            databaseId: 'default',
            timestamp: new Date().toISOString(),
            projectId: 'default',
            environment: 'development',
            endpoints: []
          });
        }
      } else {
        setSystemStatus(prev => ({
          ...prev,
          api: 'offline',
          database: 'disconnected',
          functions: 'error',
          lastCheck: new Date().toLocaleString('nl-NL')
        }));
      }
    } catch (error) {
      setSystemStatus(prev => ({
        ...prev,
        api: 'offline',
        database: 'disconnected',
        functions: 'error',
        lastCheck: new Date().toLocaleString('nl-NL')
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkSystemHealth();
    
    if (autoRefresh) {
      const interval = setInterval(checkSystemHealth, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [currentUser, autoRefresh]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
      case 'connected':
      case 'deployed':
        return 'bg-green-100 text-green-800';
      case 'offline':
      case 'disconnected':
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'loading':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
      case 'connected':
      case 'deployed':
        return '✅';
      case 'offline':
      case 'disconnected':
      case 'error':
        return '❌';
      case 'loading':
        return '⏳';
      default:
        return '❓';
    }
  };

  const formatUptime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}u ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getMetricColor = (value: number, type: 'cpu' | 'memory' | 'response') => {
    if (type === 'response') {
      if (value < 200) return 'text-green-600';
      if (value < 500) return 'text-yellow-600';
      return 'text-red-600';
    }
    
    if (value < 50) return 'text-green-600';
    if (value < 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
            Health Dashboard
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Systeem status en prestatie monitoring 🏥
          </p>
          <p className="text-sm text-gray-500">
            Real-time overzicht van de gezondheid van je licentieserver
          </p>
        </div>

        {/* Controls */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={checkSystemHealth}
              disabled={loading}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 flex items-center space-x-2"
            >
              <span>{loading ? '⏳' : '🔄'}</span>
              <span>{loading ? 'Vernieuwen...' : 'Vernieuwen'}</span>
            </button>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="autoRefresh"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="autoRefresh" className="text-sm text-gray-600">
                Auto-vernieuwen (10s)
              </label>
            </div>
          </div>
          
          <div className="text-sm text-gray-500">
            Laatste update: {systemStatus.lastCheck}
          </div>
        </div>

        {/* System Status Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-12 translate-x-12 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">API Status</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(systemStatus.api)}`}>
                      {getStatusIcon(systemStatus.api)} {systemStatus.api === 'online' ? 'ONLINE' : systemStatus.api === 'offline' ? 'OFFLINE' : 'LADEN'}
                    </span>
                  </div>
                </div>
                <div className="text-3xl">🌐</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-green-100 to-emerald-200 rounded-full -translate-y-12 translate-x-12 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">Database</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(systemStatus.database)}`}>
                      {getStatusIcon(systemStatus.database)} {systemStatus.database === 'connected' ? 'VERBONDEN' : systemStatus.database === 'disconnected' ? 'NIET VERBONDEN' : 'LADEN'}
                    </span>
                  </div>
                </div>
                <div className="text-3xl">🗄️</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-200 rounded-full -translate-y-12 translate-x-12 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">Functies</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(systemStatus.functions)}`}>
                      {getStatusIcon(systemStatus.functions)} {systemStatus.functions === 'deployed' ? 'ACTIEF' : systemStatus.functions === 'error' ? 'FOUT' : 'LADEN'}
                    </span>
                  </div>
                </div>
                <div className="text-3xl">⚡</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-100 to-yellow-200 rounded-full -translate-y-12 translate-x-12 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">Response Time</p>
                  <p className={`text-lg font-bold mt-2 ${getMetricColor(metrics.responseTime, 'response')}`}>
                    {metrics.responseTime}ms
                  </p>
                </div>
                <div className="text-3xl">⚡</div>
              </div>
            </div>
          </div>
        </div>

        {/* Active Sessions Metric */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-100 to-pink-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">Actieve Licentie Sessies</p>
                  <p className="text-3xl font-bold text-purple-600 mt-2">
                    {metrics.activeConnections}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    📊 Real-time data uit database
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Gebruikers met actieve licenties
                  </p>
                </div>
                <div className="text-4xl">🔑</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">API Response Time</p>
                  <p className={`text-3xl font-bold mt-2 ${getMetricColor(metrics.responseTime, 'response')}`}>
                    {metrics.responseTime}ms
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    📊 Gemeten bij laatste check
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {metrics.responseTime < 200 ? 'Uitstekend' : metrics.responseTime < 500 ? 'Goed' : 'Langzaam'}
                  </p>
                </div>
                <div className="text-4xl">⚡</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-100 to-emerald-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">Server Uptime</p>
                  <p className="text-2xl font-bold text-green-600 mt-2">
                    {healthInfo ? formatUptime(healthInfo.uptime) : 'Laden...'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    📊 Tijd sinds laatste herstart
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Cloud Function instance
                  </p>
                </div>
                <div className="text-4xl">⏱️</div>
              </div>
            </div>
          </div>
        </div>

        {/* System Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
            <div className="relative z-10">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">ℹ️</span>
                Systeem Informatie
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Versie</h4>
                  <p className="text-sm text-gray-600">{healthInfo?.version || 'Laden...'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Uptime</h4>
                  <p className="text-sm text-gray-600">{healthInfo ? formatUptime(healthInfo.uptime) : 'Laden...'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Regio</h4>
                  <p className="text-sm text-gray-600">{healthInfo?.region || 'Laden...'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Node Versie</h4>
                  <p className="text-sm text-gray-600">{healthInfo?.nodeVersion || 'Laden...'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Database ID</h4>
                  <p className="text-sm text-gray-600">{healthInfo?.databaseId || 'Laden...'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Omgeving</h4>
                  <p className="text-sm text-gray-600">{healthInfo?.environment || 'Laden...'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-100 to-emerald-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
            <div className="relative z-10">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🌐</span>
                API Endpoints
              </h3>
              <div className="space-y-2 text-sm max-h-64 overflow-y-auto">
                {healthInfo?.endpoints && healthInfo.endpoints.length > 0 ? (
                  healthInfo.endpoints.map((endpoint, index) => (
                    <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
                      <span className="bg-gray-200 text-gray-800 px-2 py-1 rounded text-xs font-medium min-w-[50px] text-center">
                        {endpoint.method.toUpperCase()}
                      </span>
                      <code className="text-gray-600 flex-1">{endpoint.path}</code>
                    </div>
                  ))
                ) : (
                  // Fallback endpoints
                  [
                    { method: 'GET', path: '/health' },
                    { method: 'POST', path: '/register' },
                    { method: 'POST', path: '/activate' },
                    { method: 'POST', path: '/deactivate' },
                    { method: 'POST', path: '/validate' },
                    { method: 'GET', path: '/admin/stats' },
                    { method: 'GET', path: '/admin/health' },
                    { method: 'GET', path: '/admin/activations' },
                    { method: 'POST', path: '/admin/export' },
                    { method: 'POST', path: '/admin/cleanup' }
                  ].map((endpoint, index) => (
                    <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
                      <span className="bg-gray-200 text-gray-800 px-2 py-1 rounded text-xs font-medium min-w-[50px] text-center">
                        {endpoint.method}
                      </span>
                      <code className="text-gray-600 flex-1">{endpoint.path}</code>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Health Check URLs */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-100 to-pink-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
          <div className="relative z-10">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🔗</span>
              Health Check URLs
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Basis Health Check</h4>
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl border border-gray-200">
                  <code className="text-sm text-gray-800 break-all">
                    https://license.s3e.nl/health
                  </code>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Eenvoudige status check voor monitoring tools
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Gedetailleerde Health Check</h4>
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl border border-gray-200">
                  <code className="text-sm text-gray-800 break-all">
                    https://license.s3e.nl/admin/health
                  </code>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Uitgebreide informatie (authenticatie vereist)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Copyright Footer */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          © 2025 S3e - Health Dashboard
        </p>
      </div>
    </div>
  );
}

export default Health; 